/**
 * The docs' page on the Morcast Payment Protocol: what runs in the escrow contract and what runs
 * in the platform, the contract's functions and who may call each, what the settler and the
 * owner can and cannot do, and a campaign's deadlines with who enforces them. The statements
 * follow the V1 specification (docs/specification.md, sections 2, 5 and 11).
 */

import { styles } from "../../components/ui.js";
import { CampaignTimeline } from "./diagrams.js";
import { PROTOCOL_URL } from "./nav.js";
import { Code, DocPage, DocSection, DocTable, P, Points } from "./parts.js";

export function Protocol() {
  return (
    <DocPage
      group="Protocol"
      title="How it works"
      documentTitle="Protocol"
      lead="The Morcast Payment Protocol is the escrow contract on Base and the open rules that turn a published result into payouts. The contract stays small, and every detailed rule runs off-chain, where it is published and can be checked."
    >
      <DocSection id="split" title="What runs where">
        <P>
          The escrow contract does three things only. It holds the money, pays it out once by a
          fixed formula, and refunds the brand if Morcast does not settle.
        </P>
        <DocTable
          caption="What runs in the escrow contract and what runs in the Morcast platform"
          head={["On Base, in the escrow contract", "Off-chain, in the Morcast platform"]}
          rows={[
            [
              "Holds each campaign's budget",
              "Campaign requests, their review and the published terms",
            ],
            [
              "Settles each campaign once, between Day 5 and Day 10 after its end",
              "Creators' accounts and submissions",
            ],
            [
              "Computes the fee, the creators' pool and the refund from the recognized reach",
              "Measurement, verification and scoring",
            ],
            [
              "Pays creators' claims against a Merkle root",
              "Publication of results, review and decisions on issues",
            ],
            [
              "Refunds the whole budget if not settled before Day 10",
              "Each creator's payout and its proof",
            ],
          ]}
        />
        <P>
          The protocol is open source. Its contract, its settlement arithmetic and the tool that
          checks a result are in the{" "}
          <a href={PROTOCOL_URL} rel="noopener" className={styles.link}>
            Morcast Payment Protocol repository
          </a>
          .
        </P>
      </DocSection>

      <DocSection id="escrow" title="The escrow contract">
        <P>
          <Code>MorcastEscrow</Code> holds every campaign's budget on Base, keeps each campaign's
          accounting separate and cannot be upgraded.
        </P>
        <DocTable
          caption="The escrow contract's functions"
          mono
          head={["Function", "Who calls it", "When", "Effect"]}
          rows={[
            [
              "createCampaign",
              "Brand",
              "Before the start",
              "Deposits the budget, in USDC or MOR. The campaign is funded",
            ],
            ["cancel", "Brand", "Before the start", "Returns the budget"],
            [
              "settle",
              "Settler",
              "Day 5 to Day 10 after the end",
              "Records the recognized reach, the Merkle root and the result's hash, and fixes the fee, the pool and the refund",
            ],
            [
              "claim",
              "Anyone",
              "After settlement",
              "Pays one creator's payout to the wallet named in its proof",
            ],
            ["withdrawFee", "Anyone", "After settlement", "Pays the fee to Morcast's treasury"],
            [
              "withdrawBrand",
              "Brand",
              "After settlement, or from Day 10 if not settled",
              "Pays the refund, or the whole budget",
            ],
          ]}
        />
        <Points
          items={[
            {
              lead: "What the settler can do.",
              text: "One thing only, which is to settle each campaign once inside its window.",
            },
            {
              lead: "What the owner can do.",
              text: "The owner operates the contract. It can replace the settler and the wallet that receives the fee, pause new campaigns, allow or disallow a token, and return a funded campaign's whole budget to its brand. It can never send a campaign's money anywhere else.",
            },
            {
              lead: "What nobody can do.",
              text: "Nobody can change the formula, the fee, the deadlines or a settlement once made. A campaign can never pay out more than its own budget.",
            },
          ]}
        />
        <P>
          The contract updates its records before it transfers tokens, guards every transfer against
          reentrancy, and rejects a deposit that does not raise its balance by exactly the budget.
        </P>
        <P>
          A new version is a new deployment at a new address. Older versions keep running for their
          campaigns, and claims never expire.
        </P>
      </DocSection>

      <DocSection id="timeline" title="Campaign timeline">
        <P>
          A campaign's deadlines are fixed in advance, and the contract enforces the ones that
          protect money. Day N means N days after the campaign's end.
        </P>
        <CampaignTimeline />
        <DocTable
          caption="A campaign's deadlines and who enforces each"
          head={["Step", "When", "Enforced by"]}
          rows={[
            ["Deposit, or cancel", "Before the start", "Contract"],
            [
              "Campaign window",
              "From the start to the end, at most 90 days, and 29 days on X",
              "Contract and Morcast",
            ],
            ["Reach read on X", "At the end", "Morcast"],
            ["Reach read on YouTube", "Day 3", "Morcast"],
            ["First result published", "By Day 4", "Morcast"],
            ["Issues that can change the result", "Until Day 7", "Morcast"],
            ["Last corrected result", "By Day 8", "Morcast"],
            ["Settlement", "Day 5 to Day 10", "Contract"],
            ["Whole budget back if not settled", "From Day 10", "Contract"],
            ["Claims and withdrawals", "Any time after settlement", "Contract"],
          ]}
        />
      </DocSection>
    </DocPage>
  );
}
